import { create } from "zustand"

type ParserJobsStore = {
  activeSessionIds: Set<string>
  activeJobsById: Record<string, string>
  setActiveSessionIds: (sessionIds: string[]) => void
  addActiveSession: (sessionId: string) => void
  removeActiveSession: (sessionId: string) => void
  isSessionActive: (sessionId: string) => boolean
  addActiveJob: (jobId: string, sessionId: string) => void
  removeActiveJob: (jobId: string) => void
}

function areSetsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false

  for (const value of left) {
    if (!right.has(value)) return false
  }

  return true
}

export const useParserJobsStore = create<ParserJobsStore>((set, get) => ({
  activeSessionIds: new Set(),
  activeJobsById: {},
  setActiveSessionIds: (sessionIds) => {
    const nextSessionIds = new Set(sessionIds)
    if (areSetsEqual(get().activeSessionIds, nextSessionIds)) return
    set({ activeSessionIds: nextSessionIds })
  },
  addActiveSession: (sessionId) => {
    const currentSessionIds = get().activeSessionIds
    if (currentSessionIds.has(sessionId)) return
    set({ activeSessionIds: new Set([...currentSessionIds, sessionId]) })
  },
  removeActiveSession: (sessionId) => {
    const currentSessionIds = get().activeSessionIds
    if (!currentSessionIds.has(sessionId)) return

    const nextSessionIds = new Set(currentSessionIds)
    nextSessionIds.delete(sessionId)
    set({ activeSessionIds: nextSessionIds })
  },
  isSessionActive: (sessionId) => get().activeSessionIds.has(sessionId),
  addActiveJob: (jobId, sessionId) => {
    set((state) => {
      if (state.activeJobsById[jobId] === sessionId) return state

      const nextActiveJobsById = { ...state.activeJobsById, [jobId]: sessionId }
      const nextActiveSessionIds = state.activeSessionIds.has(sessionId)
        ? state.activeSessionIds
        : new Set([...state.activeSessionIds, sessionId])

      return { activeJobsById: nextActiveJobsById, activeSessionIds: nextActiveSessionIds }
    })
  },
  removeActiveJob: (jobId) => {
    set((state) => {
      const sessionId = state.activeJobsById[jobId]
      if (!sessionId) return state

      const nextActiveJobsById = Object.fromEntries(
        Object.entries(state.activeJobsById).filter(([key]) => key !== jobId)
      )
      const hasAnotherJobForSession = Object.values(nextActiveJobsById).some(
        (jobSessionId) => jobSessionId === sessionId
      )

      if (hasAnotherJobForSession) {
        return { activeJobsById: nextActiveJobsById }
      }

      if (!state.activeSessionIds.has(sessionId)) {
        return { activeJobsById: nextActiveJobsById }
      }

      const nextActiveSessionIds = new Set(state.activeSessionIds)
      nextActiveSessionIds.delete(sessionId)

      return { activeJobsById: nextActiveJobsById, activeSessionIds: nextActiveSessionIds }
    })
  },
}))
